import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../ui/tooltip";
import { useMemo } from "react";

/**
 * RiskMatrixSelectionModal - A modal dialog that displays a risk matrix
 * and allows users to select probability and severity values.
 */
const RiskMatrixSelectionModal = ({
  isOpen,
  setIsOpen,
  riskMatrix,
  onSelectCell,
  title = "Risk Matrix",
  description = "Click on a cell to select both probability and severity",
  riskType = "unmitigated", // 'unmitigated' or 'retained'
}) => {
  if (!riskMatrix) return null;

  // Helper function to get description or return a default message
  const getDescription = (type, index) => {
    if (!riskMatrix.descriptions) return "No description available";
    
    if (type === "severity" && riskMatrix.descriptions.severities && riskMatrix.descriptions.severities[index]) {
      return riskMatrix.descriptions.severities[index];
    }
    
    if (type === "probability" && riskMatrix.descriptions.probabilities && riskMatrix.descriptions.probabilities[index]) {
      return riskMatrix.descriptions.probabilities[index];
    }
    
    return "No description available";
  };

  // Get combined description for a cell
  const getCombinedDescription = (probIndex, sevIndex) => {
    const probDesc = getDescription("probability", probIndex);
    const sevDesc = getDescription("severity", sevIndex);
    
    const probability = riskMatrix.probabilities[probIndex];
    const severity = riskMatrix.severities[sevIndex];
    const cellValue = riskMatrix.matrix[probIndex][sevIndex];
    
    return (
      <div className="space-y-2">
        <div><strong>Risk Rating:</strong> {cellValue}</div>
        <div><strong>Probability ({probability}):</strong> {probDesc !== "No description available" ? probDesc : "No description"}</div>
        <div><strong>Severity ({severity}):</strong> {sevDesc !== "No description available" ? sevDesc : "No description"}</div>
      </div>
    );
  };

  // Create a selectable version of the matrix
  const SelectableMatrix = () => {
    return riskMatrix.isAxesFlipped ? (
      <SelectableFlippedMatrix />
    ) : (
      <SelectableNormalMatrix />
    );
  };

  // Component for normal matrix display (probability as rows, severity as columns)
  const SelectableNormalMatrix = () => {
    return (
      <div className="mt-4">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border p-2 text-xs text-center align-bottom min-w-[60px] relative" style={{height: 48}}>
                {/* Diagonal divider for axis labels */}
                <div className="w-full h-full">
                  <div className="absolute top-0 left-0 w-full h-full">
                    <div className="w-full h-full relative">
                      <div className="absolute top-0 right-0 w-1/2 h-1/2 flex items-end justify-end text-xs p-1">
                        {riskMatrix.diagonalText?.top || 'Severity'}
                      </div>
                      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 flex items-start justify-start text-xs p-1">
                        {riskMatrix.diagonalText?.bottom || 'Probability'}
                      </div>
                      <div
                        className="absolute top-0 left-0 w-full h-full"
                        style={{
                          background:
                            "linear-gradient(to top right, transparent calc(50% - 1px), black, transparent calc(50% + 1px))",
                          pointerEvents: "none",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </th>
              {riskMatrix.severities.map((label, index) => (
                <th key={index} className="border p-2 text-sm">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{label}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>{getDescription("severity", index)}</p>
                    </TooltipContent>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {riskMatrix.matrix.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="border p-2 font-medium text-sm">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{riskMatrix.probabilities[rowIndex]}</span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>{getDescription("probability", rowIndex)}</p>
                    </TooltipContent>
                  </Tooltip>
                </td>

                {row.map((cell, colIndex) => {
                  const backgroundColor = riskMatrix.colors[cell] || "#FFFFFF";
                  const textColor = getContrastColor(backgroundColor);

                  return (
                    <td
                      key={colIndex}
                      className="border p-2 text-center text-sm cursor-pointer transition-all duration-200 relative group"
                      style={{
                        backgroundColor,
                        color: textColor,
                      }}
                      onClick={() => {
                        onSelectCell(
                          riskMatrix.probabilities[rowIndex],
                          riskMatrix.severities[colIndex]
                        );
                        setIsOpen(false);
                      }}
                    >
                      <div 
                        className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"
                        aria-hidden="true"
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help relative z-10">{cell}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {getCombinedDescription(rowIndex, colIndex)}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Component for flipped matrix display (severity as rows, probability as columns)
  const SelectableFlippedMatrix = () => {
    // Create a transposed version of the matrix for display
    const transposedMatrix = useMemo(() => {
      return riskMatrix.matrix[0].map((_, colIndex) => 
        riskMatrix.matrix.map(row => row[colIndex])
      );
    }, []);

    return (
      <div className="mt-4">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border p-2 text-xs text-center align-bottom min-w-[60px] relative" style={{height: 48}}>
                {/* Diagonal divider for axis labels (flipped) */}
                <div className="w-full h-full">
                  <div className="absolute top-0 left-0 w-full h-full">
                    <div className="w-full h-full relative">
                      <div className="absolute top-0 right-0 w-1/2 h-1/2 flex items-end justify-end text-xs p-1">
                        {riskMatrix.diagonalText?.top || 'Probability'}
                      </div>
                      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 flex items-start justify-start text-xs p-1">
                        {riskMatrix.diagonalText?.bottom || 'Severity'}
                      </div>
                      <div
                        className="absolute top-0 left-0 w-full h-full"
                        style={{
                          background:
                            "linear-gradient(to top right, transparent calc(50% - 1px), black, transparent calc(50% + 1px))",
                          pointerEvents: "none",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </th>
              {riskMatrix.probabilities.map((label, index) => (
                <th key={index} className="border p-2 text-sm">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{label}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>{getDescription("probability", index)}</p>
                    </TooltipContent>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {transposedMatrix.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="border p-2 font-medium text-sm">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{riskMatrix.severities[rowIndex]}</span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>{getDescription("severity", rowIndex)}</p>
                    </TooltipContent>
                  </Tooltip>
                </td>

                {row.map((_, colIndex) => {
                  // Use the original matrix cell value
                  const actualCell = riskMatrix.matrix[colIndex][rowIndex];
                  const backgroundColor = riskMatrix.colors[actualCell] || "#FFFFFF";
                  const textColor = getContrastColor(backgroundColor);

                  return (
                    <td
                      key={colIndex}
                      className="border p-2 text-center text-sm cursor-pointer transition-all duration-200 relative group"
                      style={{
                        backgroundColor,
                        color: textColor,
                      }}
                      onClick={() => {
                        onSelectCell(
                          riskMatrix.probabilities[colIndex],
                          riskMatrix.severities[rowIndex]
                        );
                        setIsOpen(false);
                      }}
                    >
                      <div 
                        className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"
                        aria-hidden="true"
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help relative z-10">{actualCell}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {getCombinedDescription(colIndex, rowIndex)}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{title} - {riskMatrix.name}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <SelectableMatrix />
          </div>

          <DialogFooter>
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

// Helper function to determine text color based on background color
const getContrastColor = (backgroundColor) => {
  // Remove any leading # from hex color
  const hex = backgroundColor.replace("#", "");

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black or white based on luminance
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

export default RiskMatrixSelectionModal;